import { Link } from "react-router-dom";
import { Shield, Smartphone, Lock, Eye, Users, AlertTriangle, CheckCircle, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const TIPS = [
  {
    category: "Online-Sicherheit",
    icon: Smartphone,
    color: "#BDE0FE",
    tips: [
      {
        title: "Sichere Passwörter verwenden",
        description: "Nutze lange Passwörter mit Zahlen und Sonderzeichen. Nicht dasselbe Passwort für alles!",
        emoji: "🔐"
      },
      {
        title: "Privatsphäre-Einstellungen prüfen",
        description: "Stelle deine Social-Media-Profile auf privat. Nicht jeder muss alles sehen.",
        emoji: "🔒"
      },
      {
        title: "Keine persönlichen Infos teilen",
        description: "Adresse, Schule, Telefonnummer - das gehört nicht ins Internet für alle sichtbar.",
        emoji: "🤫"
      },
      {
        title: "Vorsicht bei Fremden",
        description: "Akzeptiere keine Freundschaftsanfragen von Leuten, die du nicht kennst.",
        emoji: "👤"
      },
    ]
  },
  {
    category: "Wenn du gemobbt wirst",
    icon: Shield,
    color: "#A0E8AF",
    tips: [
      {
        title: "Nicht antworten",
        description: "Mobber wollen eine Reaktion. Wenn du nicht antwortest, verlieren sie oft das Interesse.",
        emoji: "🚫"
      },
      {
        title: "Beweise sichern",
        description: "Mach Screenshots von gemeinen Nachrichten. Die brauchst du später als Beweis.",
        emoji: "📸"
      },
      {
        title: "Blockieren und melden",
        description: "Blockiere die Person und melde sie bei der Plattform (Instagram, TikTok, etc.).",
        emoji: "🛑"
      },
      {
        title: "Hilfe holen",
        description: "Erzähle einem Erwachsenen davon - Eltern, Lehrer, oder rufe eine Hotline an.",
        emoji: "🆘"
      },
    ]
  },
  {
    category: "Für dein Wohlbefinden",
    icon: Users,
    color: "#FFC4D6",
    tips: [
      {
        title: "Es ist nicht deine Schuld",
        description: "Egal was die Mobber sagen - DU bist nicht das Problem, sie sind es.",
        emoji: "💚"
      },
      {
        title: "Leg das Handy weg",
        description: "Nimm dir Pausen vom Internet. Tu Dinge, die dir Spaß machen.",
        emoji: "📵"
      },
      {
        title: "Rede darüber",
        description: "Mit Freunden, Familie oder einem Berater. Reden hilft!",
        emoji: "💬"
      },
      {
        title: "Du bist stark",
        description: "Dass du das durchstehst und dir Hilfe holst, zeigt wie stark du bist.",
        emoji: "💪"
      },
    ]
  },
];

export default function Selbstschutz() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Schutz vor Mobbing & Cybermobbing – Tipps fuer Kinder" description="Praktische Tipps zum Schutz vor Mobbing und Cybermobbing. Wie schuetzt du dich online? Was tust du, wenn du gemobbt wirst? Jetzt informieren." path="/selbstschutz" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#A0E8AF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-[#1A4731]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Tipps zum Selbstschutz
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              So kannst du dich im Internet schützen und mit Mobbing umgehen. 
              Diese Tipps helfen dir, sicherer zu sein.
            </p>
          </div>

          {/* Tips Sections */}
          {TIPS.map((section, sectionIndex) => {
            const Icon = section.icon;
            return (
              <div key={sectionIndex} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${section.color}30` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: section.color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>
                    {section.category}
                  </h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {section.tips.map((tip, tipIndex) => (
                    <div 
                      key={tipIndex}
                      className="bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
                    >
                      <div className="text-3xl mb-3">{tip.emoji}</div>
                      <h3 className="font-bold text-[#2D3748] mb-2">{tip.title}</h3>
                      <p className="text-[#718096] text-sm">{tip.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Quick Checklist */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-[#2D3748] mb-4 text-center" style={{ fontFamily: 'Nunito' }}>
              ✅ Schnell-Check: Bist du sicher?
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                "Meine Passwörter sind sicher",
                "Meine Profile sind privat",
                "Ich teile keine persönlichen Daten",
                "Ich weiß, wen ich bei Problemen fragen kann",
                "Ich habe Screenshots von Mobbing gesichert",
                "Ich kenne die Notfall-Hotlines",
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/60 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-[#A0E8AF] flex-shrink-0" />
                  <span className="text-[#718096] text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-[#718096] mb-4">
              Wirst du gerade gemobbt? Du bist nicht allein!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-6 py-3 rounded-full hover:bg-[#86D696] transition-all"
              >
                Jetzt melden
              </Link>
              <Link 
                to="/notfall"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#FC8181] text-[#E53E3E] font-bold px-6 py-3 rounded-full hover:bg-[#FC8181]/10 transition-all"
              >
                Notfall-Hotlines
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
